import React from "react";
import { motion } from "framer-motion";

/**
 * Reveal — scroll-triggered animation primitive.
 * Wraps children in a motion element that animates in when scrolled into view.
 *
 * Variants:
 *   - up (default), down, left, right, scale, fade
 */
const variantsMap = {
  up:    { hidden: { opacity: 0, y: 28 },           visible: { opacity: 1, y: 0 } },
  down:  { hidden: { opacity: 0, y: -28 },          visible: { opacity: 1, y: 0 } },
  left:  { hidden: { opacity: 0, x: -28 },          visible: { opacity: 1, x: 0 } },
  right: { hidden: { opacity: 0, x: 28 },           visible: { opacity: 1, x: 0 } },
  scale: { hidden: { opacity: 0, scale: 0.94 },     visible: { opacity: 1, scale: 1 } },
  fade:  { hidden: { opacity: 0 },                  visible: { opacity: 1 } },
};

const Reveal = ({
  children,
  variant = "up",
  delay = 0,
  duration = 0.7,
  once = true,
  amount = 0.2,
  className = "",
  as = "div",
  ...rest
}) => {
  const v = variantsMap[variant] || variantsMap.up;
  const MotionTag = motion[as] || motion.div;

  return (
    <MotionTag
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount, margin: "0px 0px -60px 0px" }}
      variants={v}
      transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
      {...rest}
    >
      {children}
    </MotionTag>
  );
};

/**
 * RevealGroup — staggers children using Reveal.
 * Pass children as an array; each will get a sequential delay.
 */
export const RevealGroup = ({ children, stagger = 0.08, baseDelay = 0, ...rest }) => {
  const items = React.Children.toArray(children);
  return (
    <>
      {items.map((child, i) => (
        <Reveal key={i} delay={baseDelay + i * stagger} {...rest}>
          {child}
        </Reveal>
      ))}
    </>
  );
};

export default Reveal;
